import { useState } from "react";
import { toast } from "sonner";
import { Printer, DownloadSimple, FileText, FileXls } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Th, Td, Empty, DateRange } from "@/components/common";
import AiSummary from "@/components/AiSummary";
import { CashflowChart, CashflowStats } from "@/components/CashflowCard";
import { api, money, fmtDate, num, monthStart, todayStr, downloadCSV, errMsg } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";
import { cn } from "@/lib/utils";

const REPORTS = [
  ["sales", "Satış Raporu", "Tarih aralığındaki satış faturaları"],
  ["purchases", "Alış Raporu", "Tarih aralığındaki alış faturaları"],
  ["accounts", "Cari Bakiye Raporu", "Bitiş tarihine kadar cari borç/alacak"],
  ["stock", "Stok Durum Raporu", "Güncel stok miktarı ve değeri"],
  ["stock-movements", "Stok Hareket Raporu", "Giriş ve çıkış hareketleri"],
  ["cash", "Kasa Raporu", "Tahsilat ve ödemeler"],
  ["orders", "Sipariş Raporu", "Satış ve alış siparişleri"],
  ["shipments", "Sevk Raporu", "Sevk ve mal kabul detayları"],
  ["vat", "KDV Raporu", "Oran bazında hesaplanan / indirilecek KDV"],
  ["cashflow", "Nakit Akışı Tahmini", "30/60/90 günlük beklenen tahsilat ve ödeme"],
];

const cell = (c, v) => (c.type === "money" ? money(v) : c.type === "number" ? num(v) : c.type === "date" ? fmtDate(v) : v);

export default function Reports() {
  const [type, setType] = useState("sales");
  useQueryParam("tip", setType, "sales");
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [rep] = useLoad(() => type === "cashflow" ? Promise.resolve(null) : api.get(`/reports/${type}`, { params: { start: start || undefined, end: end || undefined } }).then((r) => r.data), [type, start, end]);
  const [cf] = useLoad(() => type === "cashflow" ? api.get("/reports/cashflow-forecast").then((r) => r.data) : Promise.resolve(null), [type]);
  const meta = REPORTS.find((r) => r[0] === type);
  const numeric = (c) => c.type === "money" || c.type === "number";
  const [exporting, setExporting] = useState(false);

  const exportWorkbook = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export/excel", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `cari-stok-dokum-${todayStr()}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel dökümü indirildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setExporting(false);
  };

  return (
    <div>
      <PageHeader eyebrow={`${fmtDate(start)} — ${fmtDate(end)}`} title="Raporlar">
        <DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} prefix="report" />
        <Button variant="outline" className="rounded-[4px]" disabled={!rep} onClick={() => downloadCSV(`${type}-${start}-${end}.csv`, rep.columns, rep.rows)} data-testid="report-csv-button"><DownloadSimple /> Excel (CSV)</Button>
        <Button variant="outline" className="rounded-[4px] border-teal-600 text-teal-700" onClick={exportWorkbook} disabled={exporting} data-testid="export-excel-button"><FileXls /> {exporting ? "Hazırlanıyor…" : "Tam Döküm (Excel)"}</Button>
        <Button variant="outline" className="rounded-[4px]" onClick={() => window.print()} data-testid="report-print-button"><Printer /> Yazdır / PDF</Button>
      </PageHeader>
      <div className="no-print mb-6"><AiSummary /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <nav className="no-print panel h-fit divide-y divide-border" data-testid="report-list">
          {REPORTS.map(([k, label, desc]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`report-type-${k}`}
              className={cn("flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150", type === k ? "bg-zinc-900 text-white" : "hover:bg-muted/60")}>
              <FileText size={18} className="mt-0.5 shrink-0" />
              <span><span className="block text-sm font-semibold">{label}</span><span className={cn("block text-[11px]", type === k ? "text-zinc-400" : "text-muted-foreground")}>{desc}</span></span>
            </button>
          ))}
        </nav>
        <div className="panel min-w-0">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-display text-2xl font-bold" data-testid="report-title">{meta[1]}</h2>
            <p className="text-xs text-muted-foreground">{meta[2]} · {type === "cashflow" ? (cf ? "bugünden itibaren 90 gün" : "Yükleniyor…") : rep ? `${rep.rows.length} satır` : "Yükleniyor…"}</p>
          </div>
          {type === "cashflow" ? (cf && (
            <div data-testid="cashflow-report">
              <CashflowStats data={cf} />
              <CashflowChart data={cf} height={320} />
              <table className="w-full" data-testid="cashflow-table">
                <thead><tr><Th>Dönem</Th><Th right>Beklenen Tahsilat</Th><Th right>Beklenen Ödeme</Th><Th right>Net</Th><Th right>Tahmini Kasa</Th></tr></thead>
                <tbody>
                  {cf.buckets.map((b) => (
                    <tr key={b.key} className="even:bg-muted/30" data-testid={`cashflow-row-${b.key}`}>
                      <Td>{b.label}</Td><Td right className="text-emerald-700">{money(b.inflow)}</Td><Td right className="text-red-700">{money(b.outflow)}</Td>
                      <Td right className={b.net < 0 ? "text-red-700" : ""}>{money(b.net)}</Td><Td right className="font-semibold">{money(b.cumulative)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="report-table">
              <thead><tr>{rep?.columns.map((c) => <Th key={c.key} right={numeric(c)}>{c.label}</Th>)}</tr></thead>
              <tbody>
                {rep && rep.rows.length === 0 && <Empty cols={rep.columns.length} text="Bu aralıkta veri yok" />}
                {rep?.rows.map((r, i) => (
                  <tr key={i} className="even:bg-muted/30 hover:bg-muted/50">
                    {rep.columns.map((c) => <Td key={c.key} right={numeric(c)} className={c.type === "date" ? "font-num text-xs" : ""}>{cell(c, r[c.key])}</Td>)}
                  </tr>
                ))}
              </tbody>
              {rep && rep.rows.length > 0 && (
                <tfoot>
                  <tr className="bg-zinc-900 text-white" data-testid="report-totals">
                    {rep.columns.map((c, i) => (
                      <td key={c.key} className={cn("px-3 py-3 text-sm font-semibold", numeric(c) && "text-right font-num")}>
                        {i === 0 ? "TOPLAM" : rep.totals[c.key] !== undefined ? cell(c, rep.totals[c.key]) : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
